import { useCallback } from 'react';
import { DataType, getDataType } from '@constants';
import { COLUMNS, ROW_TYPES } from '../tableConfig';
import { CountListItem, HeaderListItem, SectionHeaderListItem } from '../types';
import { DataCountsByDataType } from 'types';

const useDataCountsByDataTypeListItemsBuilder = ({
  dataCountsByDataType,
  isCsvAgent,
}: {
  dataCountsByDataType: Partial<Record<DataType, DataCountsByDataType[]>>;
  isCsvAgent: boolean;
}) => {
  const itemBuilder = useCallback(() => {
    const lists: (SectionHeaderListItem | HeaderListItem | CountListItem)[] = [];

    // Section Headers row
    const sectionHeaderList = {
      id: ROW_TYPES.SECTION_HEADERS,
      rowType: ROW_TYPES.SECTION_HEADERS,
      disableRowHover: true,
      cellClassNameByColumns: {
        [COLUMNS.DATA_TYPE]: ['sticky', 'show-border-top', 'show-border-right', 'zindex3'],
        [COLUMNS.OPTION]: ['show-border-top', 'show-border-bottom'],
        [COLUMNS.IS_TO_MOSAIC]: ['show-border-top', 'show-border-bottom'],
        [COLUMNS.IS_TO_AGENT]: ['show-border-top'],
        [COLUMNS.IS_TO_INTEGRATION]: ['show-border-top', 'show-border-bottom'],
        [COLUMNS.REPLICA_DATABASE_TO_AGENT]: ['show-border-top', 'show-border-bottom'],
        /* -------------------------------------------------------------------------- */
        [COLUMNS.SYNCED]: ['show-border-top', 'show-border-left'],
        [COLUMNS.TOTAL_AVAILABLE_FOR_IMPORTS]: ['show-border-top'],
        [COLUMNS.TOTAL_ON_AGENT]: ['show-border-top'],
        [COLUMNS.TOTAL_ON_REPLICA_DATABASE]: ['show-border-top'],
        [COLUMNS.TOTAL_PENDING]: ['show-border-top'],
        [COLUMNS.TOTAL_ON_REPLICA_DATABASE]: ['show-border-top'],
        /* -------------------------------------------------------------------------- */
        [COLUMNS.EXCEEDED_RETRIES_TO_MOSAIC]: ['show-border-top', 'show-border-left'],
        [COLUMNS.LAST_DAYS_TO_MOSAIC]: ['show-border-top'],
        [COLUMNS.TOTAL_PERSISTING_TO_MOSAIC]: ['show-border-top'],
      },
    };

    // Headers row
    const headerListItem = {
      id: ROW_TYPES.HEADERS,
      rowType: ROW_TYPES.HEADERS,
      disableRowHover: true,
      isStickyHeader: true,
      cellClassNameByColumns: {
        [COLUMNS.DATA_TYPE]: ['sticky', 'show-border-right', 'zindex3'],
        [COLUMNS.SYNCED]: ['show-border-left'],
        [COLUMNS.EXCEEDED_RETRIES_TO_MOSAIC]: ['show-border-left'],
      },
    } as HeaderListItem;

    lists.push(sectionHeaderList as any);
    lists.push(headerListItem);

    // Counts rows
    Object.values(dataCountsByDataType || {}).forEach((data) => {
      data?.forEach((item) => {
        const {
          dataType: _dataType,
          CREATE_NEW_ON_MOSAIC,
          UPDATE_ON_MOSAIC,
          CREATE_NEW_ON_TARGET,
          UPDATE_ON_TARGET,
          active,
          subMappingStatus,
        } = item;
        const dataType = getDataType(_dataType);
        /**
         * Spec:
         * - Synced = Active Count + Update on Mosaic + Update on Target
         * - Pending on Mosaic = Create on Mosaic ONLY. Show Update on Mosaic as tooltip and a small number greyed out on cell.
         * - Pending on Target = Create on Target ONLY. Show Update on Target as tooltip and a small number greyed out on cell.
         * - Total Pending = DO NOT INCLUDE UPDATE. Main cell should only SHOW total CREATE, the small number grey out on cell show total UPDATE
         */
        const synced = +active + +UPDATE_ON_MOSAIC + +UPDATE_ON_TARGET;
        const pendingToTarget = +CREATE_NEW_ON_TARGET;
        const pendingToMosaic = +CREATE_NEW_ON_MOSAIC;
        const totalPending = pendingToTarget + pendingToMosaic; // Only create
        const totalPendingUpdate = +UPDATE_ON_MOSAIC + +UPDATE_ON_TARGET; // Only update
        const subMappingStatusLabel = subMappingStatus ? 'Null' : 'Draft';

        lists.push({
          id: _dataType,
          rowType: ROW_TYPES.DATA,
          disableRowHover: true,
          cellClassNameByColumns: {
            [COLUMNS.DATA_TYPE]: ['sticky', 'show-border-right'],
            [COLUMNS.SYNCED]: ['show-border-left'],
            [COLUMNS.EXCEEDED_RETRIES_TO_MOSAIC]: ['show-border-left'],
            [COLUMNS.OPTION]: ['show-border-right'],
          },
          data: item,
          totalPending,
          totalPendingUpdate,
          synced,
          pendingToTarget,
          pendingToMosaic,
          dataType,
          dataTypeLabel: isCsvAgent
            ? `${dataType.label} (${subMappingStatusLabel})`
            : dataType.label,
        });
      });
    });

    return lists;
  }, [dataCountsByDataType, isCsvAgent]);
  return itemBuilder;
};

export default useDataCountsByDataTypeListItemsBuilder;
